import type { RefObject } from "react";
import { useEffect, useRef } from "react";

import { getFieldLabel } from "./helpers/getFieldLabel";
import { resetLabelsStyles } from "./helpers/resetLabelsStyles";
import { setLabelsStyles } from "./helpers/setLabelsStyles";
import { useGetMaxHeightOnResize } from "./helpers/useGetMaxHeightOnResize";

type Options = {
  disableSync?: boolean;
};

const defaultOptions: Options = {
  disableSync: false,
};

/**
 * Synchronize labels height across provided fields to match the highest label height
 * This will align the fields in the same row in the form
 */
export function useSyncFieldsLabels(
  fieldContainersRefs: RefObject<HTMLElement | null>[],
  options: Options = defaultOptions
): void {
  const lastMaxHeightRef = useRef<number>(0);

  const htmlLabels = fieldContainersRefs
    .map(getFieldLabel)
    .filter((label): label is HTMLLabelElement => Boolean(label));
  const maxHeight = useGetMaxHeightOnResize(htmlLabels);
  const isSyncDisabled = Boolean(options.disableSync);
  const isMaxHeightChanged = maxHeight !== lastMaxHeightRef.current;
  const shouldSyncLabels = !isSyncDisabled && htmlLabels.length > 0 && isMaxHeightChanged;

  useEffect(() => {
    if (isSyncDisabled) {
      lastMaxHeightRef.current = 0;
      resetLabelsStyles(htmlLabels);
      return;
    }

    if (!shouldSyncLabels) {
      return;
    }

    lastMaxHeightRef.current = maxHeight;
    setLabelsStyles(htmlLabels, `${maxHeight}px`, "flex-end");
  });
}
